import { ref as dbRef, query, orderByChild, equalTo, get } from 'firebase/database';
import { db } from '../firebase';
import { compressImage, userFacingError, IMAGE_PRESETS } from './imageCompressionUtil';
import { saveBookingReceipt, hasReceipt } from './receiptUtil';

/**
 * Upload a payment screenshot for a booking.
 *
 * The image is downscaled and re-encoded first (see imageCompressionUtil.js).
 * It used to be stored exactly as the guest picked it, capped at 2000KB — so a
 * single receipt could be ~2MB inside the booking record, and every client
 * reading that date's bookings paid to download it. Compression brings a
 * typical phone screenshot under 100KB while staying readable enough for an
 * admin to verify the payment.
 *
 * The image no longer lives on the booking record at all: it goes to
 * `bookingReceipts/{bookingId}` and the booking keeps a `hasPaymentScreenshot`
 * flag, so the grids and the reservation queue stop downloading images they
 * never render. See receiptUtil.js.
 *
 * @param {File} file - The image file to upload
 * @param {string} bookingId - The booking confirmation ID
 * @returns {Promise<string>} - The base64 data URL of the stored image
 * @throws {Error} With a user-facing message for an invalid file or an unknown
 *   confirmation ID; a generic one for anything unexpected.
 */
export const uploadPaymentScreenshot = async (file, bookingId) => {
  if (!file || !bookingId) {
    throw new Error('File and booking ID are required');
  }

  try {
    // Validates the file type, downscales, and re-encodes. Throws a
    // user-facing message if the file isn't an image or can't be shrunk.
    const { dataUrl: base64Data } = await compressImage(file, IMAGE_PRESETS.RECEIPT);

    const bookingsRef = dbRef(db, 'bookings');
    const q = query(bookingsRef, orderByChild('confirmationId'), equalTo(bookingId));
    const snapshot = await get(q);

    if (!snapshot.exists()) {
      throw userFacingError('Booking not found. Please check the confirmation ID.');
    }

    // A multi-date booking is one record per date, so the receipt is attached
    // to each of them — the admin reviews whichever date they opened.
    const keys = [];
    snapshot.forEach((childSnapshot) => { keys.push(childSnapshot.key); });
    await Promise.all(keys.map(key => saveBookingReceipt(key, base64Data)));

    return base64Data;
  } catch (error) {
    console.error('Error uploading screenshot:', error);
    // Compression failures and "booking not found" already carry a message
    // worth showing; only genuinely unexpected errors get the generic one.
    if (error.isUserFacing) throw error;
    throw new Error('Failed to upload screenshot. Please try again.');
  }
};

/**
 * Check if the screenshot was already uploaded for a booking.
 *
 * Reads the flag on the booking rather than the image itself, so this costs a
 * few bytes instead of a third of a megabyte. hasReceipt() still recognises a
 * record written before the receipts were split out.
 *
 * @param {string} bookingId - The booking confirmation ID
 * @returns {Promise<boolean>} - Whether screenshot exists
 */
export const checkScreenshotExists = async (bookingId) => {
  if (!bookingId) return false;

  try {
    const bookingsRef = dbRef(db, 'bookings');
    const q = query(bookingsRef, orderByChild('confirmationId'), equalTo(bookingId));
    const snapshot = await get(q);

    if (!snapshot.exists()) return false;

    let found = false;
    snapshot.forEach((childSnapshot) => {
      if (hasReceipt(childSnapshot.val())) found = true;
    });
    return found;
  } catch (error) {
    console.error('Error checking screenshot:', error);
    return false;
  }
};
